import { BadInputError } from '@watershed/errors/BadInputError';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { WatershedError } from '@watershed/errors/WatershedError';
import {
  divideUnits,
  invertUnit,
  isNotNullUnit,
  isNullableUnitEqual,
  multiplyUnits,
  unitToString,
  type Unit,
} from '@watershed/lscript/unit';
import assertNever from '@watershed/shared-util/src/assertNever';
import { pluralize } from '../utils/helpers';

import {
  ASTCall,
  ASTComparison,
  ASTExpr,
  ASTProgram,
  ASTStmt,
  stringifyExpr,
} from '@watershed/lscript/ast';
import {
  ExprType,
  NumberType,
  isNullable,
  stringifyType,
} from '@watershed/lscript/expr_types';
import { getFunction } from './functions';
import { isNumberLiteral } from './literal';
import { TypeConversionError, convertToType } from './typeConversion';
import { Context } from './types';

export class ProgramTypecheckError extends WatershedError {
  line: ASTStmt;

  constructor(msg: string, line: ASTStmt) {
    super('BAD_INPUT', msg);
    this.line = line;
  }
}

export type TypecheckWarning = string;

export function typecheckExpression(
  ctx: Context,
  expr: ASTExpr,
  warnings?: Array<TypecheckWarning>
): ExprType {
  switch (expr.t) {
    case 'string':
      return { ty: 'string' };
    case 'number':
      return { ty: 'number' };
    case 'boolean':
      return { ty: 'boolean' };
    case 'unary':
      switch (expr.op) {
        case '-':
          const et = typecheckExpression(ctx, expr.expr, warnings);
          if (et.ty !== 'number') {
            throw new BadInputError(
              `Expected ${stringifyExpr(
                expr.expr
              )} to be a number, but got ${stringifyType(et)}`
            );
          }
          return et;
        default:
          assertNever(expr);
      }

    case 'identifier': {
      const ty = ctx.variablesType.get(expr.name);
      if (ty == null) {
        const known = [...ctx.variablesType.keys()].join(', ');
        warnings?.push(
          `Unknown variable ${expr.name}; assuming numeric${
            known.length > 0 ? ` (know about ${known})` : ''
          }`
        );
        return { ty: 'number' };
      }
      return ty;
    }

    case 'binary':
      const lType = typecheckExpression(ctx, expr.l, warnings);
      const rType = typecheckExpression(ctx, expr.r, warnings);

      const makeBinaryOpError = (msg: string) => {
        return new BadInputError(
          `In binary operation "${stringifyExpr(
            expr
          )}", ${msg}, got types ${stringifyType(
            lType
          )} (left) and ${stringifyType(rType)} (right)`
        );
      };

      if (lType.ty !== 'number' || rType.ty !== 'number') {
        throw makeBinaryOpError('both operands must be number');
      }

      let newUnit: Unit | undefined = lType.unit;
      switch (expr.op) {
        case '+':
        case '-':
          if (isNumberLiteral(expr.l) && !isNumberLiteral(expr.r)) {
            newUnit = rType.unit;
          } else if (isNumberLiteral(expr.r) && !isNumberLiteral(expr.l)) {
            newUnit = lType.unit;
          } else if (!isSameType(lType, rType)) {
            throw makeBinaryOpError(
              'the operands of addition or subtraction must have same type'
            );
          }
          break;
        case '*':
          if (isNotNullUnit(lType.unit) && isNotNullUnit(rType.unit)) {
            newUnit = multiplyUnits(lType.unit, rType.unit);
          } else if (isNotNullUnit(lType.unit)) {
            newUnit = lType.unit;
          } else if (isNotNullUnit(rType.unit)) {
            newUnit = rType.unit;
          } else {
            newUnit = undefined;
          }
          break;
        case '/':
          // The numerator must have a unit.
          if (isNotNullUnit(lType.unit) && isNotNullUnit(rType.unit)) {
            newUnit = divideUnits(lType.unit, rType.unit);
          } else if (isNotNullUnit(lType.unit)) {
            newUnit = lType.unit;
          } else if (isNotNullUnit(rType.unit)) {
            newUnit = invertUnit(rType.unit);
          } else {
            newUnit = undefined;
          }
          break;
        default:
          assertNever(expr);
      }

      if (isNotNullUnit(newUnit)) {
        return {
          ty: 'number',
          unit: newUnit,
        };
      } else {
        return {
          ty: 'number',
        };
      }

    case 'comparison': {
      assertComparableTypes(ctx, expr, warnings);
      return { ty: 'boolean' };
    }

    case 'call': {
      const name = expr.name;
      const fn = getFunction(name);
      if (fn == null) {
        throw new BadInputError(`Unknown function ${name}`);
      }
      return fn.typecheck(ctx, expr, warnings);
    }

    case 'null':
      return { ty: 'null' };

    default:
      assertNever(expr);
  }
}

export function typecheckProgram(
  ctx: Context,
  prog: ASTProgram,
  warningsByVar?: Map<string, Array<TypecheckWarning>>
): Map<string, ExprType> {
  ctx = {
    ...ctx,
    variables: new Map(ctx.variables),
  };
  const out = new Map<string, ExprType>();
  for (const [i, line] of prog.entries()) {
    let ty: ExprType | null = null;
    // do a try catch here and just grab the line

    try {
      // We should never get a dup here but just being paranoid
      const warnings = warningsByVar?.get(line.name) || [];
      ty = typecheckExpression(ctx, line.expr, warnings);
      if (warnings.length > 0) {
        warningsByVar?.set(line.name, warnings);
      }
    } catch (error) {
      if (error instanceof BadInputError) {
        throw new ProgramTypecheckError(error.message, line);
      } else {
        throw error;
      }
    }

    if (ty === null) {
      throw new UnexpectedError(
        `Typechecker returned a null expression type for expression ${stringifyExpr(
          line.expr
        )}`
      );
    }

    const lineTy = line.typeDeclaration;
    if (!lineTy) {
      // If the line / variable don't have an explicit type declaration, the
      // type of the variable is the type of the expression.
      out.set(line.name, ty);
      ctx.variablesType.set(line.name, ty);
      continue;
    }

    try {
      convertToType(ctx, { fromExpr: line.expr, toType: lineTy });
    } catch (error: any) {
      if (error instanceof TypeConversionError) {
        throw new ProgramTypecheckError(
          `[Line ${i + 1}] Variable "${
            line.name
          }" is declared as a "${stringifyType(
            lineTy
          )}", but the expression has type "${stringifyType(ty)}"`,
          line
        );
      }
      throw error;
    }

    out.set(line.name, lineTy);
    ctx.variablesType.set(line.name, lineTy);
  }
  return out;
}

export function assertType(
  ctx: Context,
  expected: ExprType,
  expr: ASTExpr,
  warnings?: Array<TypecheckWarning>
): void {
  const actual = typecheckExpression(ctx, expr, warnings);
  if (!isSameType(expected, actual)) {
    warnings?.push(
      `Expected ${stringifyExpr(expr)} to have type ${stringifyType(
        expected
      )}, but got ${stringifyType(actual)}`
    );
  }
}

export function assertNumeric(
  ctx: Context,
  expr: ASTExpr,
  warnings?: Array<TypecheckWarning>
): void {
  const actual = typecheckExpression(ctx, expr, warnings);
  if (actual.ty !== 'number' && actual.ty !== 'quantity') {
    warnings?.push(
      `Expected ${stringifyExpr(
        expr
      )} to be numeric (number or quantity), but got ${stringifyType(actual)}`
    );
  }
}

/**
 * Throws an error if it finds incompatible types in a comparison expression.
 */
function assertComparableTypes(
  ctx: Context,
  expr: ASTComparison,
  warnings?: Array<TypecheckWarning>
): void {
  const op = expr.op;
  const l = typecheckExpression(ctx, expr.l, warnings);
  const r = typecheckExpression(ctx, expr.r, warnings);
  const warningMessagePrefix = `Invalid comparison \`${stringifyExpr(
    expr
  )}\`: `;

  if (!isTypeComparable(l) || !isTypeComparable(r)) {
    if (!isTypeComparable(l)) {
      warnings?.push(
        `${warningMessagePrefix}type \`${stringifyType(
          l
        )}\` (left) cannot be used with a comparison operator`
      );
    }
    if (!isTypeComparable(r)) {
      warnings?.push(
        `${warningMessagePrefix}type \`${stringifyType(
          r
        )}\` (right) cannot be used with a comparison operator`
      );
    }
    return;
  }
  if (isNumberLiteral(expr.l) && isNumberTypeWithUnit(r)) {
    return;
  }
  if (isNumberLiteral(expr.r) && isNumberTypeWithUnit(l)) {
    return;
  }
  if (expr.op === '==' || expr.op === '!=') {
    if (isNullable(l) || isNullable(r)) {
      return;
    }
  } else if (isNullable(l) || isNullable(r)) {
    if (isNullable(l)) {
      warnings?.push(
        `${warningMessagePrefix}type \`${stringifyType(
          l
        )}\` (left) cannot be used with a comparison operator \`${
          expr.op
        }\`, because the operator is not compatible with nullable types`
      );
    }
    if (isNullable(r)) {
      warnings?.push(
        `${warningMessagePrefix}type \`${stringifyType(
          r
        )}\` (right) cannot be used with a comparison operator \`${
          expr.op
        }\`, because the operator is not compatible with nullable types`
      );
    }
    return;
  }
  if (!isSameType(l, r)) {
    warnings?.push(
      `${warningMessagePrefix}types \`${stringifyType(
        l
      )}\` (left) and \`${stringifyType(
        r
      )}\` (right) are not comparable for operator \`${op}\``
    );
    return;
  }
}

function isNumberTypeWithUnit(type: ExprType): type is NumberType {
  return type.ty === 'number' && 'unit' in type && !!type.unit;
}

/**
 * Indicates whether an expression type can be used with comparison operators
 * (e.g. ==, >).
 */
function isTypeComparable(ty: ExprType): boolean {
  switch (ty.ty) {
    case 'string':
    case 'boolean':
    case 'number':
    case 'quantity':
    case 'date':
    case 'datetime':
    case 'null':
      return true;
    case 'nullable':
      return isTypeComparable(ty.baseTy);
    case 'list':
      return false;
    default:
      assertNever(ty);
  }
}

export function isSameType(a: ExprType, b: ExprType): boolean {
  switch (a.ty) {
    case 'string':
    case 'boolean':
    case 'date':
    case 'datetime':
    case 'null':
      return a.ty === b.ty;

    case 'number':
      if (b.ty !== 'number') {
        return false;
      }

      return isNullableUnitEqual(a.unit, b.unit);
    case 'quantity':
      if (b.ty !== 'quantity') return false;
      // Would be cool to have a way to compare unit sets without stringifying
      // them all
      const aUnits = new Set(a.units.map(unitToString));
      const bUnits = new Set(b.units.map(unitToString));
      if (aUnits.size !== bUnits.size) {
        return false;
      }
      for (const aUnit of aUnits) {
        if (!bUnits.has(aUnit)) {
          return false;
        }
      }
      return true;
    case 'list':
      if (b.ty !== 'list') {
        return false;
      }
      return isSameType(a.elemTy, b.elemTy);
    case 'nullable':
      if (b.ty !== 'nullable') {
        return false;
      }
      return isSameType(a.baseTy, b.baseTy);
    default:
      assertNever(a);
  }
}

export function assertArgShape(
  call: ASTCall,
  min: number,
  max: number = Infinity
): void {
  if (call.args.length < min || call.args.length > max) {
    let expect: string;
    if (min === max) {
      expect = pluralize(min, 'argument');
    } else if (max === Infinity) {
      expect = `at least ${pluralize(min, 'argument')}`;
    } else {
      expect = `${min}-${max} arguments`;
    }
    throw new BadInputError(
      `Function \`${call.name}\` expects ${expect}, but received ${call.args.length}.`
    );
  }
}

export function assertKwargShape(
  call: ASTCall,
  required: Array<string>,
  extraOk: boolean,
  optional: Array<string> = [],
  context?: string
): void {
  const extraKeys = new Set(call.kwargs.keys());
  for (const arg of required) {
    if (!call.kwargs.has(arg)) {
      throw new BadInputError(
        `Function ${call.name} expects a keyword argument ${arg}` +
          `${
            context ? ' when accessing ' + context : ''
          }, but did not receive one. ` +
          `Required keys are: ${required.join(', ')}.`
      );
    }
    extraKeys.delete(arg);
  }

  for (const arg of optional) {
    extraKeys.delete(arg);
  }

  if (!extraOk && extraKeys.size > 0) {
    const extra = Array.from(extraKeys).join(', ');
    throw new BadInputError(
      `Function ${call.name} got unexpected keyword arguments ` +
        `${extra}${
          context ? ' when accessing ' + context : ''
        }. Valid keys are: ${required.concat(optional).join(', ')}.`
    );
  }
}

export function assertNoKwargs(call: ASTCall): void {
  BadInputError.invariant(
    call.kwargs.size === 0,
    `Function \`${call.name}\` does not accept keyword arguments.`
  );
}
